import {Component, Type} from '@angular/core';
import {GenericCrudEditComponent} from '../../../../../../libs/components/generic-crud/generic-crud-edit.component';
import {
  Currency,
  DraftableStateTypes, EDocumentTypeNature,
  ProcessSheets,
  Product, ProductStateTypeColors,
  ProductTreeElement, TId
} from '../../../../../../models';
import { FormGroup } from '@angular/forms';
import {firstValueFrom, take} from "rxjs";
import { ProcessSheetsResource, ProductTreeResource} from "../../../../../../resources";
import { ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import {TabRole} from "../../../../../../libs/components/tabs-host/tabs.service";
import {
  ProductsProcessSheetsEditComponent
} from "../../../products-process-sheets/products-process-sheets-edit/products-process-sheets-edit.component";
import {Scrolling} from "devextreme/ui/data_grid";
import {ProductsEditComponent} from "../../products-edit/products-edit.component";
import {ProductPricesComponent} from "../product-prices/product-prices.component";
import {ProductStockComponent} from "../product-stock/product-stock.component";
import {ProductProcesSheetsComponent} from "../../product-proces-sheets/product-proces-sheets.component";
import {ProductSimulationsComponent} from "../../product-simulations/product-simulations.component";
import {ProductSubscribersComponent} from "../product-subscribers/product-subscribers.component";


@Component({
  selector: 'app-product-map',
  standalone: false,
  templateUrl: './product-map.component.html',
  styleUrl: './product-map.component.sass'
})


export class ProductMapComponent extends GenericCrudEditComponent<Product> {

  productTreeResource?: ProductTreeResource;
  productResource = this.api.resources.product.products;
  processSheetResource?: ProcessSheetsResource;
  currency?: Currency;
  @ViewChild('gridProductTree', { static: false })
  gridProductTree?: DxDataGridComponent;
  stateTypes: any
  private selectedProductTreeMatchIndex = -1;
  editProcessSheetComponent = ProductsProcessSheetsEditComponent;
  productEditComponent = ProductsEditComponent;
  processSheetsRevisions : ProcessSheets[] = [];
  scrolling: Scrolling = { mode: 'virtual', columnRenderingMode: 'standard' };
  paging = { enabled: true };
  selectedProductTreeElement?: ProductTreeElement;
  isProcessSheetSidebarOpen = false;
  ProductStateTypeColors = ProductStateTypeColors;
  productFamilyPrefixes: string[] = [];
  selectedProductFamilyPrefixes: string[] = [];
  productIds: TId[] = [];
  ready = false;
  showAllVersions = false;



  gridHeight = 600;

  protected override async preFetch(): Promise<void> {
    if(this.entity){ this.productIds = [this.entity!.id!];}

    this.productFamilyPrefixes = await firstValueFrom(this.api.productFamiliesPrefixes());

    const prefixes = this.productIds.length === 0 ? this.productFamilyPrefixes : this.selectedProductFamilyPrefixes;
    this.productTreeResource = new ProductTreeResource(
      this.api,
      [...this.productIds],
      [...prefixes],
    );

    this.currency = this.storage.companyCurrency();

    this.stateTypes = DraftableStateTypes;
    this.stateTypes.forEach((x: any) =>
      x.displayName = this.translationService.instant(`enumeration.DraftableStateTypes.${x.name}`)
    );

    const screenHeight = window.innerHeight;


    if (screenHeight > 1200) {
      this.gridHeight = 900;
    } else if (screenHeight > 900) {
      this.gridHeight = 800;
    }

    this.ready = true;



  }
  private getLinesWithIsRawMaterial(): ProductTreeElement[] {
    if (!this.gridProductTree?.instance) {
      return [];
    }

    return this.gridProductTree.instance
      .getDataSource()
      .items()
      .filter((row: ProductTreeElement) => row.isMatchingProduct);
  }

  getInputPrms(row: any): any {
    const innerEditors: any[] = []
    innerEditors.push({
      name: 'price',
      component: ProductPricesComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name: 'stock',
      component: ProductStockComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name:'processsheets',
      component: ProductProcesSheetsComponent,
      inputPrms: { productId: row }
    })
    innerEditors.push({
      name:'simulations',
      component: ProductSimulationsComponent,
      inputPrms: { id: row }
    })
    if (this.acl.isAdmin()) {
      innerEditors.push({
        name: 'subscribers',
        component: ProductSubscribersComponent,
        inputPrms: { id: row },
        isAdminConfig: true
      });
    }
    return {
      //productFamilies: this.productFamilies,
      id: row,
      innerEditors: innerEditors,
    };
  }

  private selectProductTreeRow(row: ProductTreeElement): void {
    if (!row || !this.gridProductTree?.instance) {
      return;
    }

    this.clearProcessSheetCheckboxAndSidebar();
    const grid = this.gridProductTree.instance;
    const key = (row as any).treeOrder;


    grid.selectRows([key], false);

    grid.navigateToRow(key).then(() => {
      const rowIndex = grid.getRowIndexByKey(key);
      const cellElement = grid.getCellElement(rowIndex, 0);

      if (cellElement) {
        grid.focus(cellElement);
      }
    });
  }

  selectFirstProductCodeMatch(): void {
    const matches = this.getLinesWithIsRawMaterial();

    if (!matches.length) return;

    this.selectedProductTreeMatchIndex = 0;
    this.selectProductTreeRow(matches[this.selectedProductTreeMatchIndex]);
  }

  selectLastProductCodeMatch(): void {
    const matches = this.getLinesWithIsRawMaterial();

    if (!matches.length) return;

    this.selectedProductTreeMatchIndex = matches.length - 1;
    this.selectProductTreeRow(matches[this.selectedProductTreeMatchIndex]);
  }

  selectNextProductCodeMatch(): void {
    const matches = this.getLinesWithIsRawMaterial();

    if (!matches.length) return;

    if (this.selectedProductTreeMatchIndex < 0) {
      this.selectedProductTreeMatchIndex = 0;
    } else {
      this.selectedProductTreeMatchIndex = Math.min(
        this.selectedProductTreeMatchIndex + 1,
        matches.length - 1
      );
    }

    this.selectProductTreeRow(matches[this.selectedProductTreeMatchIndex]);
  }

  selectPreviousProductCodeMatch(): void {
    const matches = this.getLinesWithIsRawMaterial();

    if (!matches.length) return;

    if (this.selectedProductTreeMatchIndex < 0) {
      this.selectedProductTreeMatchIndex = matches.length - 1;
    } else {
      this.selectedProductTreeMatchIndex = Math.max(
        this.selectedProductTreeMatchIndex - 1,
        0
      );
    }

    this.selectProductTreeRow(matches[this.selectedProductTreeMatchIndex]);
  }

  navigateToProcessSheetEditor(element: ProcessSheets) {
    this.saveGridScrollPosition();

    const cmpt = this.tabs.createTab<GenericCrudEditComponent<ProcessSheets>>(
      this.editProcessSheetComponent!,
      TabRole.EDITION,
      { inputPrms: { entity: element } }
    );

    cmpt.component.instance.destroy$
      .pipe(take(1))
      .subscribe(() => {
        setTimeout(() => this.restoreGridVirtualScroll(), 200);
      });
  }

  openProduct(element: ProductTreeElement) {
    this.saveGridScrollPosition();

    const cmpt = this.tabs.createTab<GenericCrudEditComponent<Product>>(
      this.productEditComponent!,
      TabRole.EDITION,
      { inputPrms: this.getInputPrms(element.productId) }
    );

    cmpt.component.instance.destroy$
      .pipe(take(1))
      .subscribe(() => {
        setTimeout(() => this.restoreGridVirtualScroll(), 200);
      });
  }

  async openProcessSheet(element: ProductTreeElement){

    const processSheet: ProcessSheets = await firstValueFrom(this.api.resources.product.processSheetsRevisions(element.processSheetId, element.processSheetVersionId).get(element.processSheetRevisionId)) as unknown as ProcessSheets;

    this.navigateToProcessSheetEditor(processSheet)
  }

  private productMapScrollTop = 0;

  private saveGridScrollPosition(): void {
    const scrollable = this.gridProductTree?.instance.getScrollable();

    if (!scrollable) {
      return;
    }

    this.productMapScrollTop = scrollable.scrollTop();
  }

  private restoreGridVirtualScroll(): void {
    const grid = this.gridProductTree?.instance;
    const scrollable = grid?.getScrollable();

    if (!grid || !scrollable) {
      return;
    }

    const targetTop = this.productMapScrollTop;

    grid.updateDimensions();
    scrollable.update();

    setTimeout(() => {
      scrollable.scrollTo({ top: targetTop });

      setTimeout(() => {
        grid.updateDimensions();
        scrollable.update();
        grid.repaint();
        scrollable.scrollTo({ top: targetTop });
      }, 100);
    }, 50);
  }


  selectedCheckboxTreeOrder?: any;

  async toggleProcessSheetSidebarFromCheckbox(cell: any, checked: boolean): Promise<void> {
    const row = cell.data as ProductTreeElement;

    if (!checked) {
      this.selectedCheckboxTreeOrder = undefined;
      this.selectedProductTreeElement = undefined;
      this.isProcessSheetSidebarOpen = false;
      this.showAllVersions = false;
      this.processSheetsRevisions = [];
      this.gridProductTree?.instance.clearSelection();
      return;
    }

    const key = row.treeOrder;

    this.selectedCheckboxTreeOrder = key;
    this.selectedProductTreeElement = row;
    this.isProcessSheetSidebarOpen = true;

    this.gridProductTree?.instance.selectRows([key], false);

    this.processSheetsRevisions = await firstValueFrom(
      this.api.getProcessSheetListRevisionsForProduct(row.productId)
    );
  }

  private clearProcessSheetCheckboxAndSidebar(): void {
    this.selectedCheckboxTreeOrder = undefined;
    this.selectedProductTreeElement = undefined;
    this.isProcessSheetSidebarOpen = false;
    this.showAllVersions = false;
    this.processSheetsRevisions = [];
  }

  closeProcessSheetSidebar(): void {
    this.selectedCheckboxTreeOrder = undefined;
    this.selectedProductTreeElement = undefined;
    this.isProcessSheetSidebarOpen = false;
    this.showAllVersions = false;
    this.processSheetsRevisions = [];
    this.gridProductTree?.instance.clearSelection();
  }

  getProcessSheetStateColor(stateId?: number): string {
    return ProductStateTypeColors[stateId as keyof typeof ProductStateTypeColors] ?? '#adadad';
  }
  getProcessSheetStateName(stateId?: number): string {
    return this.stateTypes.find((x: any) => x.id === stateId)?.displayName ?? '';
  }

  get visibleProcessSheetsRevisions(): ProcessSheets[] {
    if (this.showAllVersions) {
      return this.processSheetsRevisions;
    }

    return this.processSheetsRevisions.filter(revision =>
      revision.revisionStateId === 1 || revision.revisionStateId === 2
    );
  }

  productValueChanged(productIds: TId[]) {
    this.selectedProductTreeMatchIndex = -1;

    if (this.selectedProductFamilyPrefixes.length === 0 && productIds.length === 0) {
      this.productTreeResource?.setProductFamilyPrefixesAndProductIds(
        productIds,
        this.productFamilyPrefixes
      );
    } else {
      this.productTreeResource?.setProductIds(productIds);
    }

    this.gridProductTree?.instance.getDataSource()?.reload().then(() => {
      this.selectFirstProductCodeMatch();
    });
  }

  productFamilyPrefixesChanged(prefixes: string[]): void {
    this.selectedProductFamilyPrefixes = prefixes;

    if(prefixes.length === 0 && this.productIds.length === 0) {
      this.productTreeResource?.setProductFamilyPrefixesAndProductIds(this.productIds, this.productFamilyPrefixes);
    } else {
      this.productTreeResource?.setProductFamilyPrefixes(prefixes);
    }

    this.gridProductTree?.instance.refresh();
  }

  override initFormGroup(): FormGroup {
       return this.fb.group({})
    }
    override displayEntity(): string | [string, string] | { title: { text: string; icon?: string; color?: string; }; subtitle?: { text: string; icon?: string; color?: string; }; } {
        return '';
    }

  protected readonly DocTypeNatures = EDocumentTypeNature;
}
